/* eslint-disable @typescript-eslint/no-empty-function */
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { DashboardCard, Flex, P } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { BodyText, TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'
import { RootState } from 'store'

type ShiftRowData = {
  name: string
  value: number
  color: string
}

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`

const WorkersMeta = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ theme }) => theme.greyText};
  white-space: nowrap;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  gap: 0;
`

const ShiftBlock = styled.div`
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.thinSeparator};

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 4px;
    padding-bottom: 4px;
  }
`

const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin-bottom: 8px;
`

const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const Bullet = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 1px;
  flex-shrink: 0;
  background: ${(p) => p.$color};
`

const ShiftName = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.greyText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowRight = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-shrink: 0;
`

const ShiftValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textColor};
  min-width: 1.25rem;
  text-align: right;
`

const ShiftPct = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colorLightGray};
  min-width: 2rem;
  text-align: right;
`

const TrackWrap = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
`

const Track = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.textColor};
  opacity: 0.1;
  border-radius: 2px;
`

const Fill = styled.div<{ $color: string; $pct: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(p) => p.$pct}%;
  max-width: 100%;
  background: ${(p) => p.$color};
  border-radius: 2px;
`

const Footer = styled.div`
  width: 100%;
  margin-top: 6px;
  padding-top: 4px;
`

const Timeline = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 0 2px;
`

const TimeSlot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
  max-width: 33%;
`

const Tick = styled.div`
  width: 18px;
  height: 2px;
  background: ${({ theme }) => theme.colorOrange};
  border-radius: 1px;
`

const TimeLabel = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.greyText};
`

const FooterCaption = styled.p`
  margin: 10px 0 0;
  text-align: center;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.greyText};
`

function shiftColor(hex: string | undefined): string {
  if (!hex) {
    return '#ff9900'
  }
  const trimmed = hex.trim()
  if (/^#[0-9A-Fa-f]{6}$/i.test(trimmed)) {
    return trimmed
  }
  if (/^[0-9A-Fa-f]{6}$/i.test(trimmed)) {
    return `#${trimmed}`
  }
  return '#ff9900'
}

export const HrShiftsWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const { shiftsSummary } = useSelector((state: RootState) => state.hrDashboard)

  const rows = useMemo((): ShiftRowData[] => {
    const sorted = shiftsSummary?.shifts
      ? [...shiftsSummary.shifts]
          .filter((s) => s.numberOfUsers > 0)
          .sort((a, b) => b.numberOfUsers - a.numberOfUsers)
      : []

    return sorted.slice(0, 3).map((s) => ({
      name: s.name,
      value: s.numberOfUsers,
      color: shiftColor(s.color),
    }))
  }, [shiftsSummary?.shifts])

  const total = useMemo(() => rows.reduce((acc, r) => acc + r.value, 0), [rows])

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => {
        onDragStart('shifts', 1)
      }}
      onDrop={onDragDrop}
      onDragEnd={(e) => {
        e.preventDefault()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('shifts')
      }}
      width='1fr'
      style={{ gridArea: 'shifts', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('shifts')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}
      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0, flex: 1, minWidth: 0 }}>
          SHIFT DISTRIBUTION
        </TitleText>
        {rows.length > 0 && <WorkersMeta>{total} workers</WorkersMeta>}
      </CardHeader>
      <Content>
        {rows.length === 0 ? (
          <Flex width='100%' style={{ paddingTop: '4px' }}>
            <BodyText>No shifts available</BodyText>
          </Flex>
        ) : (
          <>
            {rows.map((row, i) => {
              const pct = total > 0 ? Math.round((row.value / total) * 100) : 0
              return (
                <ShiftBlock key={`${row.name}-${i}`}>
                  <RowTop>
                    <RowLeft>
                      <Bullet $color={row.color} />
                      <ShiftName>{row.name}</ShiftName>
                    </RowLeft>
                    <RowRight>
                      <ShiftValue>{row.value}</ShiftValue>
                      <ShiftPct>{pct}%</ShiftPct>
                    </RowRight>
                  </RowTop>
                  <TrackWrap>
                    <Track />
                    <Fill $color={row.color} $pct={pct} />
                  </TrackWrap>
                </ShiftBlock>
              )
            })}
          </>
        )}
      </Content>
    </DashboardCard>
  )
}
